import { useEffect, useMemo } from "react";
import { Platform } from "@-/com.npmjs.react-native";
import {
  getDeviceSetting,
  setDeviceSetting,
  useDeviceAccount,
  useDeviceSetting,
} from "@-/dev.improve.haniverse.lib.device-settings";
import type { AuthSessionSnapshot, AuthSessionUser } from "#auth-session-snapshot";

/**
 * The account this device is studying under. The native app cannot be entered without one (the
 * `(app)` layout sends the device to the welcome gate), so every screen inside it has an email to
 * show, even before any account exists on the server.
 *
 * - `signedIn`: a Better Auth session exists; `user` is its user.
 * - `unverified`: an anonymous local account. Its address came from "Get Started" and has not been
 *   confirmed yet: signing up with it and entering the emailed code activates it.
 * - `sessionExpired`: this device was signed in (it still holds the account's sync credentials), but
 *   its session expired or was revoked. Logging in again resumes it.
 *
 * Both of the latter exist only on native. Web has no anonymous account: a browser without a session
 * is signed out, whatever address it last used.
 */
export type LocalAccount =
  | { email: string; status: "signedIn"; user: AuthSessionUser }
  | { email: string; status: "sessionExpired" | "unverified"; user: null };

/**
 * The local account for a session read. Shared by the real `useLocalAccount` and the signed-out
 * test double's, so tests run the same account logic as the app.
 */
export function useLocalAccountForSession({ user }: AuthSessionSnapshot): LocalAccount | null {
  const [accountEmail] = useDeviceSetting("accountEmail");
  const [deviceAccount] = useDeviceAccount();
  const hasDeviceAccount = deviceAccount != null;
  return useMemo(() => {
    if (user != null) {
      return { email: user.email, status: "signedIn", user };
    }
    if (accountEmail == null || Platform.OS === "web") {
      return null;
    }
    return {
      email: accountEmail,
      status: hasDeviceAccount ? "sessionExpired" : "unverified",
      user: null,
    };
  }, [accountEmail, hasDeviceAccount, user]);
}

/** Keeps `accountEmail` equal to a signed-in session's address (see `useRecordSessionAccountEmail`). */
export function useRecordAccountEmailForSession({ user }: AuthSessionSnapshot): void {
  const sessionEmail = user?.email;
  useEffect(() => {
    if (sessionEmail != null && sessionEmail !== getDeviceSetting("accountEmail")) {
      setDeviceSetting("accountEmail", sessionEmail);
    }
  }, [sessionEmail]);
}
